import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, BookOpen, DollarSign, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl w-full text-center">
        {/* 404 Graphic */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-32 h-32 bg-emerald-500/10 rounded-full mb-6">
            <Search className="w-16 h-16 text-emerald-500" />
          </div>
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-4">404</h1>
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-300 mb-4">
            Page Not Found
          </h2>
          <p className="text-lg text-gray-400 max-w-xl mx-auto mb-8">
            Oops! It looks like this page expired... just like an options contract. 
            Let's get you back on track with our educational content.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
            <Link to="/">
              <Home className="w-5 h-5 mr-2" />
              Go to Homepage
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/analogies">
              <BookOpen className="w-5 h-5 mr-2" />
              Browse Analogies
            </Link>
          </Button>
        </div>

        {/* Popular Pages */}
        <div className="glass rounded-2xl p-8">
          <h3 className="text-xl font-bold text-white mb-6">Popular Learning Resources</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/csp">
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition cursor-pointer h-full">
                <CardHeader>
                  <DollarSign className="w-8 h-8 text-emerald-500 mb-2" />
                  <CardTitle className="text-white text-lg">Cash-Secured Puts</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm">
                    Learn CSPs through simple analogies
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/covered-calls">
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition cursor-pointer h-full">
                <CardHeader>
                  <TrendingUp className="w-8 h-8 text-emerald-500 mb-2" />
                  <CardTitle className="text-white text-lg">Covered Calls</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm">
                    Understand the covered call strategy
                  </p>
                </CardContent>
              </Card>
            </Link>

            <Link to="/wheel">
              <Card className="bg-gray-900/50 border-gray-800 hover:border-emerald-500 transition cursor-pointer h-full">
                <CardHeader>
                  <svg className="w-8 h-8 text-emerald-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <CardTitle className="text-white text-lg">The Wheel Strategy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm">
                    Master the complete wheel cycle
                  </p>
                </CardContent>
              </Card>
            </Link>
          </div>

          {/* Additional Links */}
          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-400 mb-4">Or explore more:</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/math-risk" className="text-emerald-400 hover:text-emerald-300 text-sm underline">
                Options Calculators
              </Link>
              <Link to="/glossary" className="text-emerald-400 hover:text-emerald-300 text-sm underline">
                Options Glossary
              </Link>
              <Link to="/analogies" className="text-emerald-400 hover:text-emerald-300 text-sm underline">
                All Analogies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
